import { useEffect, useState, useRef } from "react";
import { Star, Quote, Plus, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, Divider, Overline } from "@/components/layout/Atoms";
import { api, formatApiError } from "@/lib/api";

function StarRow({ value, onClick, size = 18 }) {
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type={onClick ? "button" : undefined}
                    disabled={!onClick}
                    onClick={onClick ? () => onClick(n) : undefined}
                    className={`${onClick ? "cursor-pointer" : "cursor-default"} transition-transform ${onClick ? "hover:scale-110" : ""}`}
                    aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                >
                    <Star size={size} className={n <= value ? "fill-kayama-gold text-kayama-gold" : "text-kayama-line"} />
                </button>
            ))}
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }); } catch { return ""; }
}

function ReviewForm({ onSuccess, onCancel }) {
    const [form, setForm] = useState({ author_name: "", rating: 5, comment: "", event_type: "", website: "" });
    const [submitting, setSubmitting] = useState(false);
    const openedAtRef = useRef(Date.now());

    async function submit(e) {
        e.preventDefault();
        if (!form.author_name.trim() || form.comment.trim().length < 5) {
            toast.error("Merci de renseigner votre nom et un commentaire d'au moins 5 caractères.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/reviews", {
                author_name: form.author_name.trim(),
                rating: form.rating,
                comment: form.comment.trim(),
                event_type: form.event_type || null,
                website: form.website,
                elapsed_ms: Date.now() - openedAtRef.current,
            });
            toast.success("Merci ! Votre avis sera publié après validation.");
            onSuccess?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <form onSubmit={submit} className="bg-kayama-paper border border-kayama-line p-8" data-testid="review-form">
            {/* Honeypot — bots fill, humans don't. Hidden from screen readers + tabs. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", height: 0, width: 0, overflow: "hidden" }}>
                <label>Site web</label>
                <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
            </div>
            <div className="flex items-center justify-between mb-6">
                <Overline>Laissez votre avis</Overline>
                {onCancel && (
                    <button type="button" onClick={onCancel} className="text-kayama-brown-3 hover:text-kayama-pink" data-testid="review-form-close">
                        <X size={20} />
                    </button>
                )}
            </div>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                <div>
                    <label className="label-k">Votre nom *</label>
                    <input className="input-k" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} required data-testid="review-name" />
                </div>
                <div>
                    <label className="label-k">Type d'événement (optionnel)</label>
                    <input className="input-k" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} placeholder="Mariage, baptême, séminaire…" data-testid="review-event-type" />
                </div>
                <div className="md:col-span-2">
                    <label className="label-k">Note</label>
                    <StarRow value={form.rating} onClick={(v) => setForm({ ...form, rating: v })} size={28} />
                </div>
                <div className="md:col-span-2">
                    <label className="label-k">Votre commentaire *</label>
                    <textarea rows={4} className="input-k resize-none" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Partagez votre expérience avec Kayama Saveurs…" required data-testid="review-comment" />
                </div>
            </div>
            <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-xs text-kayama-brown-3">Votre avis sera publié après validation de notre équipe.</p>
                <button type="submit" disabled={submitting} className="btn-primary-k disabled:opacity-60" data-testid="review-submit">
                    {submitting ? "Envoi…" : "Publier mon avis"}
                </button>
            </div>
        </form>
    );
}

export default function Reviews() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);

    async function load() {
        try {
            const { data } = await api.get("/reviews");
            setItems(data || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    const hasItems = items.length > 0;
    const avg = hasItems ? items.reduce((s, r) => s + r.rating, 0) / items.length : 0;

    return (
        <section className="container-k py-24" data-testid="reviews-section">
            <div className="text-center max-w-2xl mx-auto">
                <Overline>Témoignages</Overline>
                <h2 className="mt-4 font-heading text-4xl md:text-5xl font-light text-kayama-brown leading-tight">
                    Ils ont vécu <span className="italic text-kayama-pink">l'expérience Kayama</span>.
                </h2>
                {hasItems && (
                    <div className="mt-4 flex items-center justify-center gap-3">
                        <StarRow value={Math.round(avg)} size={20} />
                        <span className="font-heading text-2xl text-kayama-brown">{avg.toFixed(1)}</span>
                        <span className="text-xs text-kayama-brown-3 uppercase tracking-[0.2em]">/ 5 · {items.length} avis</span>
                    </div>
                )}
            </div>

            <Divider />

            {loading ? (
                <p className="text-center text-kayama-brown-3 py-10">Chargement des avis…</p>
            ) : hasItems ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                    {items.slice(0, 6).map((r, i) => (
                        <Reveal key={r.id} delay={i * 100}>
                            <article className="bg-kayama-paper border border-kayama-line p-7 h-full flex flex-col" data-testid={`review-${r.id}`}>
                                <Quote size={22} className="text-kayama-gold" />
                                <p className="mt-4 text-kayama-brown leading-relaxed italic">« {r.comment} »</p>
                                <div className="mt-6 pt-5 border-t border-kayama-line">
                                    <StarRow value={r.rating} size={14} />
                                    <p className="font-heading text-lg text-kayama-brown mt-2">{r.author_name}</p>
                                    <p className="overline mt-1">
                                        {r.event_type ? `${r.event_type} · ` : ""}{formatDate(r.created_at)}
                                    </p>
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>
            ) : (
                <div className="bg-kayama-paper border border-kayama-line p-10 text-center max-w-xl mx-auto">
                    <Quote size={28} className="text-kayama-gold mx-auto" />
                    <p className="mt-4 font-heading text-2xl text-kayama-brown">Soyez le premier à témoigner.</p>
                    <p className="mt-2 text-sm text-kayama-brown-2">Votre avis aide d'autres clients à découvrir Kayama Saveurs.</p>
                </div>
            )}

            <div className="mt-12 text-center">
                {!showForm ? (
                    <button onClick={() => setShowForm(true)} className="btn-ghost-k" data-testid="open-review-form">
                        <Plus size={14} /> Laisser un avis
                    </button>
                ) : (
                    <div className="max-w-3xl mx-auto text-left mt-6">
                        <ReviewForm onSuccess={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />
                    </div>
                )}
            </div>
        </section>
    );
}

export { ReviewForm, StarRow };
